import React, { Fragment, FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Button } from "primereact/button";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faLanguage, faMessages } from "@fortawesome/pro-regular-svg-icons";
import { faExternalLink, faImages, faPlus, faSignalStream, faSquareList } from "@fortawesome/pro-solid-svg-icons";

import "./FeatureTilesTab.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { FeatureTileDto } from "../../../../../@api";

import { useFixedLanguages } from "../../../../../utils/hooks/fixedLanguagesHook";
import { enabledFeatures } from "../../../../features";
import { Features } from "../../../../features/feature.enum";
import { translationKeys } from "../../../../models/app-translation-keys";
import { useFeatureTilesService } from "../../../../services/ServiceHooks";
import { useStore } from "../../../../stores/StoreContext";
import { TextMarkdown, TileCard } from "../../../Shared/Components";
import { AddFeatureTile } from "./AddFeatureTile/AddFeatureTile";
import { FeatureTileEditorDialog } from "./FeatureTileEditorDialog/FeatureTileEditorDialog";

const featureKeys = translationKeys.adminArea.featureTiles;
const ICONS: Record<string, IconProp> = {
  [Features.ChatBot]: faMessages,
  [Features.ImageGeneration]: faImages,
  [Features.Transcription]: faSignalStream,
  [Features.Summary]: faSquareList,
  [Features.Translation]: faLanguage,
  [Features.SchornBox]: faExternalLink,
  externalLink: faExternalLink,
};

const getFeatureTranslationKeys = (featureId: Features) => {
  switch (featureId) {
    case Features.ChatBot:
      return {
        title: translationKeys.feature.chatbot.landing.title,
        description: translationKeys.feature.chatbot.landing.description,
        buttonText: translationKeys.feature.chatbot.landing.buttonLabel,
      };
    case Features.ImageGeneration:
      return {
        title: translationKeys.feature.imageGeneration.landing.title,
        description: translationKeys.feature.imageGeneration.landing.description,
        buttonText: translationKeys.feature.imageGeneration.landing.buttonLabel,
      };
    case Features.Transcription:
      return {
        title: translationKeys.feature.transcription.landing.title,
        description: translationKeys.feature.transcription.landing.description,
        buttonText: translationKeys.feature.transcription.landing.buttonLabel,
      };
    case Features.Summary:
      return {
        title: translationKeys.feature.summary.landing.title,
        description: translationKeys.feature.summary.landing.description,
        buttonText: translationKeys.feature.summary.landing.buttonLabel,
      };
    case Features.Translation:
      return {
        title: translationKeys.feature.translation.landing.title,
        description: translationKeys.feature.translation.landing.description,
        buttonText: translationKeys.feature.translation.landing.buttonLabel,
      };
    case Features.SchornBox:
      return {
        title: translationKeys.feature.schornbox.landing.title,
        description: translationKeys.feature.schornbox.landing.description,
        buttonText: translationKeys.feature.schornbox.landing.buttonLabel,
      };
    default:
      return {
        title: translationKeys.feature.chatbot.landing.title,
        description: translationKeys.feature.chatbot.landing.description,
        buttonText: translationKeys.feature.chatbot.landing.buttonLabel,
      };
  }
};

function SortableTile(props: { tile: FeatureTileDto; onEdit: () => void; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.tile.id ?? "",
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="feature-tile-sortable-item">
      <TileCard
        tile={props.tile}
        isExternalUrl={true}
        featureIcon={props.tile.iconKey ? (ICONS[props.tile.iconKey] ?? faExternalLink) : faExternalLink}
        showAdminControls={true}
        onClick={props.onEdit}
        dragHandleProps={{ ...attributes, ...listeners }}
        onEdit={props.onEdit}
        onRemove={props.onRemove}
        customColors={{
          textColor: props.tile.colorText,
          backgroundColor: props.tile.colorBackground,
          backgroundHoverColor: props.tile.colorBackgroundHover,
        }}
      />
    </div>
  );
}

export const FeatureTilesTab: FunctionComponent = () => {
  const featureTilesService = useFeatureTilesService();

  const { t } = useTranslation();
  const { permissionStore } = useStore();
  const multiTranslate = useFixedLanguages();

  const [tiles, setTiles] = useState<FeatureTileDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [showEditor, setShowEditor] = useState(false);
  const [editingTile, setEditingTile] = useState<FeatureTileDto | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    (async () => {
      setLoading(true);
      let data = await featureTilesService.getAllFeatureTiles();
      setTiles(data);
      setLoading(false);
    })();
  }, []);

  const orderedTiles = useMemo(() => tiles.slice().sort((a, b) => (a?.order ?? 0) - (b?.order ?? 1)), [tiles]);
  const orderedIds = useMemo(() => orderedTiles.map(t => t.id ?? ""), [orderedTiles]);
  const nextOrder = useMemo(() => (tiles.length ? Math.max(...tiles.map(t => t.order ?? 0)) + 1 : 0), [tiles]);

  // reorders existing "tiles" inside the list and reassigns "order" field in each list item on FE
  // and sends the updated "tiles" to the BE
  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedIds.indexOf(String(active.id));
    const newIndex = orderedIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    const newOrderIds = arrayMove(orderedIds, oldIndex, newIndex);

    const byId = new Map(tiles.map(t => [t.id, t] as const));
    const reorderedFeatureTiles: FeatureTileDto[] = newOrderIds
      .map((id, index) => ({ ...byId.get(id)!, order: index }))
      .filter(Boolean);

    setTiles(reorderedFeatureTiles);
    await featureTilesService.reorderFeatureTiles(reorderedFeatureTiles);
  };

  const onRemove = async (id: string | undefined | null) => {
    if (!id) {
      return;
    }

    await featureTilesService.deleteFeatureTile(id);
    setTiles(await featureTilesService.getAllFeatureTiles());
  };

  const onHide = () => {
    setEditingTile(null);
    setShowEditor(false);
  };

  const onCreate = () => {
    setEditingTile(null);
    setShowEditor(true);
  };

  const onEdit = (tile: FeatureTileDto) => {
    setEditingTile(tile);
    setShowEditor(true);
  };

  const onSave = async (tile: FeatureTileDto) => {
    setIsSaving(true);

    try {
      if (editingTile && editingTile.id) {
        await featureTilesService.updateFeatureTile(editingTile.id, tile);
      } else {
        const maxOrder = tiles.length ? Math.max(...tiles.map(t => t.order ?? 0)) : -1;
        await featureTilesService.createFeatureTile({ ...tile, order: maxOrder + 1 });
      }
      setShowEditor(false);
      setEditingTile(null);
    } catch (error) {
      console.error("[FeatureTilesTab] Error occurred while saving feature tile:", error);
    } finally {
      setIsSaving(false);
      setTiles(await featureTilesService.getAllFeatureTiles());
    }
  };

  const hasTiles = orderedTiles.length > 0;

  const featureTitleText = () => {
    const result: Record<Features, any> = {} as any;

    enabledFeatures().forEach(feature => {
      const keys = getFeatureTranslationKeys(feature.id);
      result[feature.id] = multiTranslate(keys.title);
    });

    return result;
  };

  const featureDescText = () => {
    const result: Record<Features, any> = {} as any;
    enabledFeatures().forEach(feature => {
      const keys = getFeatureTranslationKeys(feature.id);
      result[feature.id] = multiTranslate(keys.description);
    });
    return result;
  };

  const featureButtonText = () => {
    const result: Record<Features, any> = {} as any;
    enabledFeatures().forEach(feature => {
      const keys = getFeatureTranslationKeys(feature.id);
      result[feature.id] = multiTranslate(keys.buttonText);
    });
    return result;
  };

  const predefinedFeatures = useMemo(
    () => enabledFeatures().filter(feature => permissionStore.hasRole(feature.role)),
    [permissionStore.userPermissions],
  );
  const hasPredefinedTiles = predefinedFeatures.length > 0;

  return (
    <div className="feature-tiles-tab">
      <div className="feature-tiles-header">
        <h1>{t(featureKeys.tabHeader)}</h1>
        <TextMarkdown>{t(featureKeys.tabDescriptionMarkdown)}</TextMarkdown>
      </div>

      {tiles.length > 5 && (
        <div className="feature-tiles-actions">
          <Button
            label={t(featureKeys.actions.create.name)}
            onClick={onCreate}
            icon={<FontAwesomeIcon icon={faPlus} />}
          />
        </div>
      )}

      {loading ? (
        <div>{t(translationKeys.global.loadingPage)}</div>
      ) : (
        <Fragment>
          {!hasTiles ? (
            <div className="features features-empty">
              <AddFeatureTile onClick={onCreate} />
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <div className={`features features-${predefinedFeatures.length + orderedTiles.length + 1}`}>
                {/* not inside SortableContext not to be draggable */}
                {hasPredefinedTiles &&
                  predefinedFeatures.map(feature => {
                    return (
                      <Fragment key={`predefined-${feature.id}`}>
                        <TileCard
                          tile={{
                            id: `predefined-${feature.id}`,
                            title: featureTitleText()[feature.id],
                            description: featureDescText()[feature.id],
                            buttonText: featureButtonText()[feature.id],
                            tooltip: { en: "", de: "", fr: "" },
                            iconKey: feature.id,
                            colorText: "#1F2D3D",
                            colorBackground: "#FFFFFF",
                            colorBackgroundHover: "#FFFFFF8F",
                            linkUrl: {
                              en: feature.menuItem.externalUrl ?? feature.menuItem.url ?? "",
                              de: feature.menuItem.externalUrl ?? feature.menuItem.url ?? "",
                              fr: feature.menuItem.externalUrl ?? feature.menuItem.url ?? "",
                            },
                            enabled: true,
                            order: 0,
                            showFooter: false,
                          }}
                          featureIcon={ICONS[feature.id] ?? faExternalLink}
                          showAdminControls={false}
                          disabled={true}
                          isExternalUrl={!!feature.menuItem.externalUrl}
                        />
                      </Fragment>
                    );
                  })}

                <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
                  {orderedTiles.map(tile => (
                    <Fragment key={tile.id}>
                      <SortableTile tile={tile} onEdit={() => onEdit(tile)} onRemove={() => onRemove(tile.id)} />
                    </Fragment>
                  ))}
                </SortableContext>

                {/* not inside SortableContext not to be draggable */}
                <AddFeatureTile onClick={onCreate} />
              </div>
            </DndContext>
          )}
        </Fragment>
      )}
      <FeatureTileEditorDialog
        tile={editingTile}
        defaultOrder={editingTile?.order ?? nextOrder}
        isSaving={isSaving}
        isVisible={showEditor}
        onCancel={onHide}
        onSave={onSave}
      />
    </div>
  );
};
